import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence } from "framer-motion";
import {
  PencilSquareIcon,
  TrashIcon,
  ChevronLeftIcon,
  FolderOpenIcon,
} from "@heroicons/react/24/outline";
import { Button, Badge } from "../../ui";
import { statusBadgeVariant } from "./constants";
import ControlFormModal from "./ControlFormModal";
import ControlDocumentsModal from "./ControlDocumentsModal";
import ConfirmDialog from "./ConfirmDialog";
import AssignSelectedControlsModal from "./AssignSelectedControlsModal";
import { useSelector } from "react-redux";
import { DataTable } from "../../common/DataTable";

/**
 * Controls drill-down view for a single framework.
 *
 * Props:
 *   framework        – selected FrameworkMaster object { id, name }
 *   controls         – array of ControlMaster objects
 *   loading          – boolean
 *   pagination       – { page, pageSize, total, totalPages }
 *   onBack           – () => void
 *   onLoadControls   – (page, pageSize, search) => void
 *   onSaveControl    – (form) => Promise<void>
 *   onDeleteControl  – (id) => Promise<void>
 */
const ControlsView = ({
  framework,
  controls,
  loading,
  pagination,
  canManageControls = true,
  sourceType = "framework-master",
  onBack,
  onLoadControls,
  onSaveControl,
  onDeleteControl,
  onAssignSelectedControls,
}) => {
  const userDetails = useSelector((state) => state.user);
  const userRole = userDetails?.user?.role || "";
  const canSelectMultipleControls =
    (userRole === "customer_admin" || userRole === "security_analyst") &&
    sourceType === "customer-framework";
  const showAssignAndStatus = userRole !== "customer_admin";
  const [search, setSearch] = useState("");
  const [localFilters, setLocalFilters] = useState({ title: "", category: "", owner: "", assigned_to: "", status: "" });
  const [modal, setModal] = useState({ open: false, data: null });
  const [confirm, setConfirm] = useState({ open: false, id: null, title: "" });
  const [docsPanel, setDocsPanel] = useState({ open: false, control: null });
  const [selectedControls, setSelectedControls] = useState([]);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const searchTimer = useRef(null);
  const lastRequestedPageRef = useRef({ page: pagination.page, pageSize: pagination.pageSize });

  useEffect(() => {
    lastRequestedPageRef.current = { page: pagination.page, pageSize: pagination.pageSize };
  }, [pagination.page, pagination.pageSize]);

  const triggerSearch = (q) => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      lastRequestedPageRef.current = { page: 1, pageSize: pagination.pageSize };
      onLoadControls(1, pagination.pageSize, q);
    }, 350);
  };

  const handleDelete = async () => {
    await onDeleteControl(confirm.id);
    setConfirm({ open: false, id: null, title: "" });
  };

  const handleRemoveSelectedControl = (controlId) => {
    setSelectedControls((prev) => prev.filter((selected) => selected.id !== controlId));
  };

  const handleAssignSelected = async (assignPayload) => {
    if (!onAssignSelectedControls) return;
    await onAssignSelectedControls(assignPayload);
    setSelectedControls([]);
    setAssignModalOpen(false);
  };

  const displayedControls = controls.filter((c) => {
    if (localFilters.title && !String(c.title || "").toLowerCase().includes(localFilters.title.toLowerCase())) return false;
    if (localFilters.category && c.category !== localFilters.category) return false;
    if (localFilters.owner && (c.control_owner || "—") !== localFilters.owner) return false;
    if (userRole !== "admin" && userRole !== "platform_admin" && localFilters.status && (c.implementation_status || "—") !== localFilters.status) return false;
    if (showAssignAndStatus && localFilters.assigned_to) {
      const names = (c.assign_details || []).map((a) => a.user_details?.name).filter(Boolean).join(", ");
      if (!names.toLowerCase().includes(localFilters.assigned_to.toLowerCase())) return false;
    }
    return true;
  });

  const uniqueCategories = [...new Set(controls.map((c) => c.category).filter(Boolean))];
  const uniqueOwners = [...new Set(controls.map((c) => c.control_owner || "—"))];
  const uniqueStatuses = [...new Set(controls.map((c) => c.implementation_status || "—"))];

  const columns = [
    {
      id: "control_id",
      accessorFn: (c) => c.control_id,
      header: "Control ID",
      enableSorting: false,
      meta: { filter: { variant: "text", label: "Control ID", placeholder: "Search by ID, title or category" } },
      cell: ({ row }) => (
        <span className="font-mono text-xs" style={{ color: "var(--app-primary-color, #26256b)" }}>
          {row.original.control_id}
        </span>
      ),
    },
    {
      id: "title",
      accessorFn: (c) => c.title,
      header: "Title",
      enableSorting: false,
      meta: { filter: { variant: "text", label: "Title", placeholder: "Search title" } },
      cell: ({ row }) => (
        <div className="max-w-xs">
          <p className="font-medium text-gray-900">{row.original.title}</p>
          {row.original.description && (
            <p className="text-xs text-gray-400 mt-0.5">{row.original.description}</p>
          )}
        </div>
      ),
    },
    {
      id: "category",
      accessorFn: (c) => c.category,
      header: "Category",
      enableSorting: false,
      meta: {
        filter: {
          variant: "select",
          label: "Category",
          placeholder: "All categories",
          options: uniqueCategories.map((v) => ({ value: v, label: v })),
        },
      },
      cell: ({ row }) => row.original.category && <Badge variant="info" size="sm">{row.original.category}</Badge>,
    },
    {
      id: "owner",
      accessorFn: (c) => c.control_owner || "—",
      header: "Owner",
      enableSorting: false,
      meta: {
        filter: {
          variant: "select",
          label: "Owner",
          placeholder: "All owners",
          options: uniqueOwners.map((v) => ({ value: v, label: v })),
        },
      },
      cell: ({ row }) => <span className="text-gray-500 text-xs">{row.original.control_owner || "—"}</span>,
    },
    ...(showAssignAndStatus
      ? [
          {
            id: "assigned_to",
            accessorFn: (c) => (c.assign_details || []).map((a) => a.user_details?.name).filter(Boolean).join(", "),
            header: "Assign To",
            enableSorting: false,
            meta: { filter: { variant: "text", label: "Assign To", placeholder: "Search assignee" } },
            cell: ({ row }) => {
              const c = row.original;
              return c.assigned_to_customer && c.assign_details?.length > 0 ? (
                <>
                  {c.assign_details.map((assign, index) => (
                    <span key={index}>
                      {assign.user_details?.name}
                      {index !== c.assign_details.length - 1 ? ", " : ""}
                    </span>
                  ))}
                </>
              ) : (
                <>-</>
              );
            },
          },
        ]
      : []),
    ...(userRole !== "admin" && userRole !== "platform_admin"
      ? [
          {
            id: "status",
            accessorFn: (c) => c.implementation_status || "—",
            header: "Status",
            enableSorting: false,
            meta: {
              filter: {
                variant: "select",
                label: "Status",
                placeholder: "All statuses",
                options: uniqueStatuses.map((v) => ({ value: v, label: v })),
              },
            },
            cell: ({ row }) => (
              <Badge variant={statusBadgeVariant(row.original.implementation_status)} size="sm">
                {row.original.implementation_status || "—"}
              </Badge>
            ),
          },
        ]
      : []),
    {
      id: "actions",
      header: "Actions",
      enableSorting: false,
      cell: ({ row }) => {
        const c = row.original;
        return (
          <div className="flex items-center gap-2">
            <button
              type="button"
              title="View Documents"
              onClick={() => setDocsPanel({ open: true, control: c })}
              className="cv-action-btn p-1.5 text-gray-400 rounded-lg transition-colors"
            >
              <FolderOpenIcon className="w-4 h-4" />
            </button>
            {userRole !== "end_user" && (
              <button
                type="button"
                title="Edit"
                onClick={() => setModal({ open: true, data: c })}
                className="cv-action-btn p-1.5 text-gray-400 rounded-lg transition-colors"
              >
                <PencilSquareIcon className="w-4 h-4" />
              </button>
            )}
            {canManageControls && (
              <button
                type="button"
                title="Delete"
                onClick={() => setConfirm({ open: true, id: c.id, title: c.title })}
                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            )}
          </div>
        );
      },
    },
  ];

  const columnFilters = [
    search ? { id: "control_id", value: search } : null,
    localFilters.title ? { id: "title", value: localFilters.title } : null,
    localFilters.category ? { id: "category", value: localFilters.category } : null,
    localFilters.owner ? { id: "owner", value: localFilters.owner } : null,
    showAssignAndStatus && localFilters.assigned_to ? { id: "assigned_to", value: localFilters.assigned_to } : null,
    userRole !== "admin" && userRole !== "platform_admin" && localFilters.status ? { id: "status", value: localFilters.status } : null,
  ].filter(Boolean);

  const handleColumnFiltersChange = (updater) => {
    const next = typeof updater === "function" ? updater(columnFilters) : updater;
    const byId = Object.fromEntries(next.map((f) => [f.id, f.value]));
    const nextSearch = byId.control_id || "";
    setSearch(nextSearch);
    triggerSearch(nextSearch);
    setLocalFilters({
      title: byId.title || "",
      category: byId.category || "",
      owner: byId.owner || "",
      assigned_to: byId.assigned_to || "",
      status: byId.status || "",
    });
  };

  return (
    <div>
      <style>{`
        .cv-action-btn:hover { color: var(--app-primary-color, #26256b) !important; background-color: color-mix(in srgb, var(--app-primary-color, #26256b) 10%, white) !important; }
      `}</style>
      {/* breadcrumb + header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="ghost"
            size="md"
            icon={ChevronLeftIcon}
            onClick={onBack}
          >
            Frameworks
          </Button>
          <span className="text-gray-300 text-lg">/</span>
          <h2 className="text-lg font-semibold text-gray-900">
            {framework.name}
            <span className="ml-2 text-sm font-normal text-gray-500">
              ({pagination.total} controls)
            </span>
          </h2>
        </div>
        {(canManageControls || canSelectMultipleControls) && (
          <div className="flex items-center gap-2">
            {canSelectMultipleControls && selectedControls.length > 0 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setAssignModalOpen(true)}
              >
                Assign Selected ({selectedControls.length})
              </Button>
            )}
          </div>
        )}
      </div>

      {/* table */}
      <DataTable
        columns={columns}
        data={displayedControls}
        getRowId={(c) => String(c.id)}
        loading={loading}
        manualPagination
        rowCount={pagination.total}
        pagination={{ pageIndex: (pagination.page || 1) - 1, pageSize: pagination.pageSize }}
        onPaginationChange={(updater) => {
          const current = { pageIndex: (pagination.page || 1) - 1, pageSize: pagination.pageSize };
          const next = typeof updater === "function" ? updater(current) : updater;
          const nextPage = next.pageIndex + 1;
          // Every column-filter change (even a purely client-side one like
          // Category/Owner) makes the table reset its own pageIndex to 0,
          // which lands here too. Only hit the backend when the page/size
          // actually differs from what's already loaded - otherwise this
          // would reload page 1 (unfiltered) on every filter keystroke and
          // stomp the client-side filtering done via displayedControls.
          if (nextPage === lastRequestedPageRef.current.page && next.pageSize === lastRequestedPageRef.current.pageSize) {
            return;
          }
          lastRequestedPageRef.current = { page: nextPage, pageSize: next.pageSize };
          onLoadControls(nextPage, next.pageSize, search);
        }}
        enableColumnFilters
        manualFiltering
        columnFilters={columnFilters}
        onColumnFiltersChange={handleColumnFiltersChange}
        emptyMessage="No controls found"
        enableColumnVisibility={false}
        enableColumnResizing
        maxBodyHeight="65vh"
        enableRowSelection={canSelectMultipleControls}
        rowSelection={Object.fromEntries(selectedControls.map((s) => [String(s.id), true]))}
        onRowSelectionChange={(updater) => {
          const currentSelection = Object.fromEntries(selectedControls.map((s) => [String(s.id), true]));
          const nextSelection = typeof updater === "function" ? updater(currentSelection) : updater;
          const nextIds = Object.keys(nextSelection).filter((id) => nextSelection[id]);
          setSelectedControls((prev) => {
            const byId = Object.fromEntries(prev.map((s) => [String(s.id), s]));
            return nextIds.map((id) => {
              if (byId[id]) return byId[id];
              const control = controls.find((c) => String(c.id) === id);
              return control
                ? {
                    id: control.id,
                    control_id: control.control_id,
                    assigned_to_customer: control.assigned_to_customer,
                    due_date: control.due_date,
                    assign_details: control.assign_details,
                  }
                : { id };
            });
          });
        }}
      />

      {/* modals */}

        <AnimatePresence>
          {modal.open && (
            <ControlFormModal
              open={modal.open}
              initial={modal.data}
              onSave={onSaveControl}
              onClose={() => setModal({ open: false, data: null })}
              sourceType={sourceType}
            />
          )}
        </AnimatePresence>

      <ControlDocumentsModal
        open={docsPanel.open}
        control={docsPanel.control}
        sourceType={sourceType}
        onClose={() => {
          setDocsPanel({ open: false, control: null });
          // File upload/approve/reject inside this panel can change the
          // control's auto-derived implementation_status - refresh the
          // list so it reflects the latest value without a manual reload.
          onLoadControls(pagination.page, pagination.pageSize, search);
        }}
      />

      {canManageControls && (
        <AnimatePresence>
          {confirm.open && (
            <ConfirmDialog
              open={confirm.open}
              title="Delete Control"
              message={`Are you sure you want to delete "${confirm.title}"? This action cannot be undone.`}
              onConfirm={handleDelete}
              onCancel={() => setConfirm({ open: false, id: null, title: "" })}
            />
          )}
        </AnimatePresence>
      )}

      <AssignSelectedControlsModal
        isOpen={assignModalOpen}
        onClose={() => setAssignModalOpen(false)}
        selectedControls={selectedControls}
        onRemoveControl={handleRemoveSelectedControl}
        onAssign={handleAssignSelected}
      />
    </div>
  );
};

export default ControlsView;
